/**
 * Reusable section wrapper for mini-modal layouts.
 * Provides an optional section title styled like itinerary panel section labels.
 */

import React, { type ReactNode } from "react";
import SectionLabel from "../shared/SectionLabel";

interface MiniModalSectionProps {

    /** Optional section title shown above the section content. */
    "title"?: string;

    /** Enables dark palette variants for section chrome. */
    "isDarkUi": boolean;

    /** Optional additional classes applied to the section wrapper. */
    "className"?: string;

    /** Section content. */
    "children": ReactNode;
}

/**
 * Standardized section container for mini-modal content blocks.
 *
 * @remarks Section titles reuse {@link SectionLabel}. Vertical rhythm
 * between sibling sections uses `mt-3` (`first:mt-0` so the leading block stays flush).
 *
 * @param props Section rendering options and children.
 * @returns Styled section element.
 */
const MiniModalSection: React.FC<MiniModalSectionProps> = ( {
    title,
    isDarkUi,
    className = "",
    children,
} ) => {
    const hasTitle = title !== undefined && title.length > 0;
    const sectionClassName = [ "mt-3 first:mt-0", className ].
        filter( ( value ) => value.length > 0 ).
        join( " " );

    return (
        <section className={sectionClassName}>
            {hasTitle
                ? (
                    <SectionLabel
                        className="mb-2"
                        headingLevel="h3"
                        isDarkUi={isDarkUi}
                        label={title}
                    />
                )
                : null}
            {children}
        </section>
    );
};

export default MiniModalSection;
